"use client";
import { cn } from "@/lib/utils";
import { Link } from "next-view-transitions";
import { useState } from "react";
import { IoIosMenu } from "react-icons/io";
import { IoIosClose } from "react-icons/io";
import { Button } from "../button";
import { Logo } from "../Logo";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { FEATURES } from "./features-menu";

const MobileFeaturesSection = ({ onSelect }: { onSelect: () => void }) => {
  const [expanded, setExpanded] = useState(false);
  const nav = useTranslations("nav");
  const fm = useTranslations("featuresMenu");
  const locale = useLocale();
  return (
    <div className="w-full">
      <button
        type="button"
        className="flex items-center gap-2 text-[26px] text-black dark:text-white"
        onClick={() => setExpanded((v) => !v)}
      >
        <span>{nav("features")}</span>
        <svg
          viewBox="0 0 12 12"
          className={cn(
            "h-5 w-5 transition-transform",
            expanded && "rotate-180"
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="m3 4.5 3 3 3-3" />
        </svg>
      </button>
      {expanded && (
        <ul className="mt-3 ml-1 pl-4 border-l border-neutral-300 dark:border-neutral-700 flex flex-col gap-2 max-h-[55vh] overflow-y-auto pr-2">
          {FEATURES.map((f) => (
            <li key={f.slug}>
              <Link
                href={`/${locale}/features/${f.slug}`}
                onClick={onSelect}
                className="block py-1"
              >
                <div className="text-lg font-medium text-black dark:text-white">
                  {fm(f.titleKey)}
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  {fm(f.descKey)}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const MobileNavbar = ({ navItems }: any) => {
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const [showBackground, setShowBackground] = useState(false);
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations("nav");

  useMotionValueEvent(scrollY, "change", (value) => {
    setShowBackground(value > 100);
  });

  const switchLocale = (newLocale: string) => {
    const newPath = pathname.replace(`/${locale}`, `/${newLocale}`);
    router.push(newPath);
    setOpen(false);
  };

  return (
    <div
      className={cn(
        "flex justify-between bg-white dark:bg-neutral-900 items-center w-full rounded-full px-2.5 py-1.5 transition duration-200",
        showBackground &&
          "bg-neutral-50 dark:bg-neutral-900 shadow-[0px_-2px_0px_0px_var(--neutral-100),0px_2px_0px_0px_var(--neutral-100)] dark:shadow-[0px_-2px_0px_0px_var(--neutral-800),0px_2px_0px_0px_var(--neutral-800)]"
      )}
    >
      <Logo />
      <button
        type="button"
        aria-label="Open menu"
        className="p-2 -mr-2"
        onClick={() => setOpen(true)}
      >
        <IoIosMenu className="text-black dark:text-white h-6 w-6" />
      </button>
      {open && (
        <div className="fixed inset-0 bg-white dark:bg-black z-50 flex flex-col items-start justify-start space-y-10 pt-5 pb-12 text-xl text-zinc-600 overflow-y-auto">
          <div className="flex items-center justify-between w-full px-5">
            <Logo />
            <button
              type="button"
              aria-label="Close menu"
              className="p-2 -mr-2"
              onClick={() => setOpen(false)}
            >
              <IoIosClose className="h-8 w-8 text-black dark:text-white" />
            </button>
          </div>
          <div className="flex flex-col items-start justify-start gap-[14px] px-8 w-full">
            <MobileFeaturesSection onSelect={() => setOpen(false)} />
            {navItems.map((navItem: any, idx: number) => (
              <Link
                key={`link-${idx}`}
                href={navItem.link}
                onClick={() => setOpen(false)}
                className="relative"
              >
                <span className="block text-[26px] text-black dark:text-white">
                  {navItem.title}
                </span>
              </Link>
            ))}
          </div>

          {/* Locale switcher */}
          <div className="flex items-center gap-3 px-8">
            <span className="text-sm text-neutral-500">Language:</span>
            <div className="flex items-center gap-1 rounded-full border border-neutral-200 dark:border-neutral-700 px-1 py-0.5">
              <button
                onClick={() => switchLocale("en")}
                className={cn(
                  "text-sm px-3 py-1 rounded-full transition-colors",
                  locale === "en"
                    ? "bg-neutral-900 dark:bg-white text-white dark:text-black font-medium"
                    : "text-neutral-500"
                )}
              >
                EN
              </button>
              <button
                onClick={() => switchLocale("de")}
                className={cn(
                  "text-sm px-3 py-1 rounded-full transition-colors",
                  locale === "de"
                    ? "bg-neutral-900 dark:bg-white text-white dark:text-black font-medium"
                    : "text-neutral-500"
                )}
              >
                DE
              </button>
            </div>
          </div>

          <div className="flex flex-row w-full items-start gap-2.5 px-8 py-4">
            <Button as={Link} href="/signup">
              {t("signup")}
            </Button>
            <Button variant="simple" as={Link} href="/login">
              {t("login")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
